import {
  kcalSplit,
  type Macros,
  type MacroTotals,
} from "@/modules/meal_plans/surfaces";

/**
 * Macros on a meal plan, as a reader sees them (H54).
 *
 * **One hue, three strengths.** Protein, carbs and fat are the accent at full,
 * 60% and 30%, never three colours: the palette has no fourth hue to give
 * (design-system.md §1), and in dark mode the brand's accent, primary and teal
 * are the same colour anyway. A ramp stays distinguishable by **lightness** in
 * both themes, and every segment is also named in words, so nothing rests on
 * colour alone (§10).
 *
 * Calories are never typed (H54): every kcal here was computed by the shared
 * `nutrition.ts`, the same function the server uses.
 */

export const MACRO_KEYS = ["protein", "carbs", "fat"] as const;
export type MacroKey = (typeof MACRO_KEYS)[number];

export const MACRO_LABEL: Record<MacroKey, string> = {
  protein: "Protein",
  carbs: "Carbs",
  fat: "Fat",
};

/** The one-letter tag an item row uses, where a word would crowd the line. */
export const MACRO_TAG: Record<MacroKey, string> = {
  protein: "P",
  carbs: "C",
  fat: "F",
};

/** The ramp. Background classes, so they work as swatches and bar segments. */
export const MACRO_FILL: Record<MacroKey, string> = {
  protein: "bg-accent",
  carbs: "bg-accent/60",
  fat: "bg-accent/30",
};

/** `41 g`, `8.6 g`: a tenth only when there is one. */
export function grams(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)} g`;
}

/** `1,850 kcal`. */
export function kcal(value: number): string {
  return `${Math.round(value).toLocaleString("en-GB")} kcal`;
}

/**
 * The sentence that keeps a partial total honest (H54): how many items the
 * total leaves out because they carry no macros. Null when it leaves none out.
 */
export function missingNote(missing: number): string | null {
  if (missing <= 0) return null;
  return missing === 1
    ? "1 item has no macros, so it is not in this total."
    : `${missing} items have no macros, so they are not in this total.`;
}

/** The calorie split as one bar. Decorative: the same figures are in words beside it. */
export function MacroSplitBar({
  totals,
  className = "h-2 w-full",
}: {
  totals: Pick<MacroTotals, "protein" | "carbs" | "fat">;
  className?: string;
}) {
  const split = kcalSplit(totals);
  return (
    <div
      aria-hidden="true"
      className={`flex shrink-0 overflow-hidden rounded-full bg-line ${className}`}
    >
      {MACRO_KEYS.map((key) =>
        split[key] > 0 ? (
          <div key={key} className={MACRO_FILL[key]} style={{ width: `${split[key]}%` }} />
        ) : null,
      )}
    </div>
  );
}

/**
 * The plan's average day (H54): the headline number, the split, and each macro
 * in grams with its share of the calories. Drawn above the days, because "what
 * does a day of this plan look like" is the first question a reader has.
 */
export function AverageDayCard({ totals }: { totals: MacroTotals }) {
  const split = kcalSplit(totals);
  const note = missingNote(totals.missing);
  return (
    <section
      aria-label="Average day"
      className="rounded-xl border border-line bg-surface p-5 shadow-sm print:border-0 print:p-0 print:shadow-none"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-bold text-ink">Average day</h2>
        <p className="font-display text-2xl font-bold text-ink">
          {kcal(totals.kcal)}
        </p>
      </div>
      <MacroSplitBar totals={totals} className="mt-3 h-2.5 w-full" />
      <dl className="mt-4 grid grid-cols-3 gap-3">
        {MACRO_KEYS.map((key) => (
          <div key={key}>
            <dt className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
              <span className={`size-2 rounded-[2px] ${MACRO_FILL[key]}`} aria-hidden="true" />
              {MACRO_LABEL[key]}
            </dt>
            <dd className="mt-0.5 tabular-nums">
              <span className="text-lg font-semibold text-ink">{grams(totals[key])}</span>
              <span className="ml-1.5 text-sm text-muted">{split[key]}%</span>
            </dd>
          </div>
        ))}
      </dl>
      {note && <p className="mt-3 text-xs text-muted">{note}</p>}
    </section>
  );
}

/**
 * A day's figure for its card header: kcal, and the split as a short bar.
 * `inline` lays the two side by side, for a header line that already holds a
 * label.
 */
export function DayMacroHeader({
  totals,
  inline = false,
}: {
  totals: MacroTotals;
  inline?: boolean;
}) {
  if (totals.counted === 0) return null;
  return (
    <div
      className={
        inline
          ? "flex shrink-0 items-center gap-2 whitespace-nowrap"
          : "flex min-w-32 flex-col items-end gap-1.5"
      }
    >
      {inline && <MacroSplitBar totals={totals} className="h-1.5 w-20" />}
      <span className="text-sm font-semibold tabular-nums text-ink">{kcal(totals.kcal)}</span>
      {!inline && <MacroSplitBar totals={totals} className="h-1.5 w-28" />}
    </div>
  );
}

/** One quiet line of totals: `457 kcal · P 41 g · C 54 g · F 8.6 g`. */
export function MacroLine({
  totals,
  className = "",
}: {
  totals: MacroTotals;
  className?: string;
}) {
  if (totals.counted === 0) return null;
  return (
    <p className={`text-xs tabular-nums text-muted ${className}`}>
      <span className="font-semibold text-body">{kcal(totals.kcal)}</span>
      {MACRO_KEYS.map((key) => (
        <span key={key}>
          {" · "}
          <span className="sr-only">{MACRO_LABEL[key]} </span>
          <span aria-hidden="true">{MACRO_TAG[key]} </span>
          {grams(totals[key])}
        </span>
      ))}
    </p>
  );
}

/** An item's own macros, as small chips after its quantity. */
export function ItemMacros({ macros, kcalValue }: { macros: Macros; kcalValue: number }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1 text-xs tabular-nums text-muted">
      {MACRO_KEYS.map((key) => (
        <span
          key={key}
          className="inline-flex items-center gap-1 rounded-md bg-line/60 px-1.5 py-0.5"
        >
          <span className={`size-1.5 rounded-[2px] ${MACRO_FILL[key]}`} aria-hidden="true" />
          <span className="sr-only">{MACRO_LABEL[key]} </span>
          <span aria-hidden="true">{MACRO_TAG[key]}</span>
          {grams(macros[key])}
        </span>
      ))}
      <span className="px-0.5">{kcal(kcalValue)}</span>
    </span>
  );
}
